/**
 * Chức vụ và quyền. Đây là NGUỒN SỰ THẬT DUY NHẤT — API dùng để chặn thật, web chỉ dùng
 * để hiển thị nhãn. Quyền hiệu lực của một tài khoản luôn do API tính rồi gửi xuống
 * (xem `CurrentUser.permissions`), web không tự suy ra.
 */
export const ROLE_CODES = [
    'DIRECTOR',
    'REVIEWER',
    'DEPARTMENT_HEAD',
    'DEPUTY_HEAD',
    'TEAM_LEAD',
    'STAFF',
];
export const ROLES = {
    DIRECTOR: {
        label: 'Giám đốc',
        shortLabel: 'GĐ',
        rank: 0,
        scope: 'all',
        leadsDepartment: false,
        seesOthers: true,
        canDelete: true,
        canConfigure: true,
        canAccessProduction: true,
        canUseAi: true,
        readOnly: false,
        personnel: false,
    },
    /**
     * NGƯỜI XEM (07/10/2026): nhìn TẤT CẢ như Giám đốc — mọi mục, mọi phòng, kể cả những mục chỉ Giám
     * đốc thấy (cài đặt, Lưu trữ, doanh thu, hồ sơ) — nhưng không đổi được gì: `readOnly` chặn ở máy
     * chủ. Không xoá, không dùng AI (mỗi lần bấm là một lần trả tiền). Cấp 1 ngang Trưởng phòng để
     * không ai dưới Giám đốc xếp lại được họ. Không thuộc phòng nào, không chấm công.
     */
    REVIEWER: {
        label: 'Người xem',
        shortLabel: 'NX',
        rank: 1,
        scope: 'all',
        leadsDepartment: false,
        seesOthers: true,
        canDelete: false,
        canConfigure: true,
        canAccessProduction: true,
        canUseAi: false,
        readOnly: true,
        personnel: false,
    },
    DEPARTMENT_HEAD: {
        label: 'Trưởng phòng',
        shortLabel: 'TP',
        rank: 1,
        scope: 'department',
        leadsDepartment: true,
        seesOthers: true,
        canDelete: true,
        canConfigure: false,
        canAccessProduction: true,
        canUseAi: true,
        readOnly: false,
        personnel: true,
    },
    /**
     * Phó phòng (chốt 23/09/2026): QUYỀN y hệt Trưởng phòng — cùng phạm vi cả phòng, cùng mọi cờ —
     * chỉ khác CẤP BẬC: dưới Trưởng phòng, trên Trưởng nhóm. Cấp bậc đó có thật ở hai chỗ: Phó phòng
     * không xếp nhóm / không gán chức vụ được cho Trưởng phòng (và cho Phó phòng khác), còn Trưởng
     * phòng thì làm được cả hai với Phó phòng của mình.
     */
    DEPUTY_HEAD: {
        label: 'Phó phòng',
        shortLabel: 'PP',
        rank: 2,
        scope: 'department',
        leadsDepartment: true,
        seesOthers: true,
        canDelete: true,
        canConfigure: false,
        canAccessProduction: true,
        canUseAi: true,
        readOnly: false,
        personnel: true,
    },
    TEAM_LEAD: {
        label: 'Trưởng nhóm',
        shortLabel: 'TN',
        rank: 3,
        scope: 'team',
        leadsDepartment: false,
        seesOthers: true,
        canDelete: false,
        canConfigure: false,
        canAccessProduction: false,
        canUseAi: false,
        readOnly: false,
        personnel: true,
    },
    STAFF: {
        label: 'Nhân viên',
        shortLabel: 'NV',
        rank: 4,
        scope: 'self',
        leadsDepartment: false,
        seesOthers: false,
        canDelete: false,
        canConfigure: false,
        canAccessProduction: false,
        canUseAi: false,
        readOnly: false,
        personnel: true,
    },
};
/**
 * Cờ quyền của một MÃ chức vụ. Mã lạ (dữ liệu cũ / dữ liệu hỏng) → mọi cờ đều false, không bao giờ
 * thành «được hết». API dùng bản nhận cả tài khoản: `hasRoleFlag` ở `permissions.ts`.
 */
export function roleHasFlag(role, flag) {
    return isRoleCode(role) ? ROLES[role][flag] : false;
}
/**
 * Có phải NHÂN SỰ không (xem `RoleDefinition.personnel`). Mã lạ → VẪN là nhân sự: một chức vụ hỏng
 * không được vì thế mà thoát chấm công hay rơi khỏi danh sách nhân sự.
 */
export function isPersonnel(role) {
    return isRoleCode(role) ? ROLES[role].personnel : true;
}
/**
 * Phải thuộc một phòng ban không. Luật «bắt buộc có phòng» sinh ra để không ai bị bỏ quên ngoài tầm
 * nhìn của mọi cấp trên; ai nhìn cả công ty (Giám đốc, Người xem) thì không có cấp trên nào phải
 * nhìn thấy họ, nên không cần phòng. Mã lạ → vẫn phải có phòng.
 */
export function requiresDepartment(role) {
    return !(isRoleCode(role) && ROLES[role].scope === 'all');
}
/** Chức vụ lạ (dữ liệu cũ / dữ liệu hỏng) đứng DƯỚI mọi chức vụ đã biết, không bao giờ trên. */
export const UNKNOWN_ROLE_RANK = 99;
export function rankOf(role) {
    return isRoleCode(role) ? ROLES[role].rank : UNKNOWN_ROLE_RANK;
}
/** Người này có đứng trên người kia không (cấp bậc THẬT SỰ cao hơn — đồng cấp trả về false). */
export function outranks(actorRole, targetRole) {
    return rankOf(actorRole) < rankOf(targetRole);
}
/**
 * Chức vụ người này gán được khi duyệt đơn: chỉ cấp THẤP HƠN mình, và không bao giờ là chức vụ nhìn
 * cả công ty (Giám đốc, Người xem). Giám đốc → Trưởng phòng · Phó phòng · Trưởng nhóm · Nhân viên;
 * Trưởng phòng → Phó phòng · Trưởng nhóm · Nhân viên; Phó phòng → Trưởng nhóm · Nhân viên. Không ai
 * tạo được người đồng cấp với mình, và không ai mở tầm nhìn cả công ty cho một đơn đăng ký — muốn
 * thêm Giám đốc hay Người xem thì cấp tài khoản mới ở Thành viên.
 */
export function assignableRolesFor(role) {
    const rank = rankOf(role);
    return ROLE_CODES.filter((code) => ROLES[code].scope !== 'all' && ROLES[code].rank > rank);
}
/** Chức vụ GIÁM ĐỐC gán được khi duyệt đơn. */
export const ASSIGNABLE_ROLES = assignableRolesFor('DIRECTOR');
export function isRoleCode(value) {
    return typeof value === 'string' && ROLE_CODES.includes(value);
}
export const ACCOUNT_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const ORG_UNIT_KINDS = ['department', 'team'];
//# sourceMappingURL=roles.js.map